import { useEffect, useRef, useState } from "react";
import { Microphone, StopCircle, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Dictado de notas con la Web Speech API (es-ES). Funciona en Chrome,
 * Edge, Samsung Internet y Safari iOS 14.5+. Si no está disponible, se
 * muestra un textarea normal.
 *
 * Props:
 *  - value: string actual
 *  - onChange(value): cambio en local (no persiste)
 *  - onSave(value): guardado (typical PUT/PATCH al expediente)
 *  - placeholder
 */
export default function VoiceDictation({ value, onChange, onSave, placeholder = "Empieza a dictar…" }) {
  const [recording, setRecording] = useState(false);
  const [supported, setSupported] = useState(true);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const recRef = useRef(null);
  const interimRef = useRef("");
  const baseRef = useRef(value || "");

  // Mantén el draft sincronizado si el padre cambia el valor (p.ej. al recargar)
  useEffect(() => {
    setDraft(value || "");
    baseRef.current = value || "";
  }, [value]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    const r = new SR();
    r.lang = "es-ES";
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (ev) => {
      let interim = "";
      let final = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const txt = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) final += txt;
        else interim += txt;
      }
      if (final) {
        baseRef.current = (baseRef.current + " " + final).replace(/\s+/g, " ").trim();
        interimRef.current = "";
      } else {
        interimRef.current = interim;
      }
      const next = (baseRef.current + (interimRef.current ? " " + interimRef.current : "")).trim();
      setDraft(next);
      onChange?.(next);
    };
    r.onerror = (e) => {
      if (e?.error && e.error !== "no-speech") {
        toast.error("Dictado: " + e.error);
      }
      setRecording(false);
    };
    r.onend = () => setRecording(false);
    recRef.current = r;
    return () => {
      try { r.stop(); } catch { /* noop */ }
    };
  }, [onChange]);

  const toggleRec = () => {
    if (!recRef.current) return;
    if (recording) {
      try { recRef.current.stop(); } catch { /* noop */ }
      setRecording(false);
    } else {
      baseRef.current = draft;
      interimRef.current = "";
      try {
        recRef.current.start();
        setRecording(true);
      } catch (e) {
        toast.error("No se pudo iniciar el micrófono");
      }
    }
  };

  const save = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(draft);
      toast.success("Notas guardadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <textarea
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          baseRef.current = e.target.value;
          onChange?.(e.target.value);
        }}
        rows={6}
        placeholder={placeholder}
        className="w-full text-[14px] leading-relaxed p-3 border border-[#E8EAED] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
        data-testid="voice-textarea"
      />
      <div className="flex items-center gap-2">
        {supported ? (
          <button
            type="button"
            onClick={toggleRec}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-3 rounded-md text-[14px] font-semibold border ${
              recording
                ? "bg-red-600 text-white border-red-600 hover:bg-red-700 animate-pulse"
                : "bg-white text-foreground border-[#E8EAED] hover:bg-zinc-50"
            }`}
            data-testid="voice-toggle"
          >
            {recording ? (
              <>
                <StopCircle className="w-5 h-5" weight="fill" />
                Detener dictado
              </>
            ) : (
              <>
                <Microphone className="w-5 h-5" weight="fill" />
                Dictar
              </>
            )}
          </button>
        ) : (
          <div className="flex-1 text-[11px] text-muted text-center">Dictado no soportado en este navegador.</div>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-4 py-3 inline-flex items-center justify-center gap-1.5 bg-emerald-600 text-white text-[14px] font-semibold rounded-md disabled:opacity-50 hover:bg-emerald-700"
          data-testid="voice-save"
        >
          <FloppyDisk className="w-4 h-4" weight="bold" />
          Guardar
        </button>
      </div>
    </div>
  );
}
